'use client';

import { ReactNode, useCallback, useEffect, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { I18N_LANGUAGES } from '@/i18n/config';
// Only statically import routable languages (de + en) to reduce initial bundle
import deTranslations from '@/i18n/messages/de.json';
import enTranslations from '@/i18n/messages/en.json';
import { DirectionProvider as RadixDirectionProvider } from '@radix-ui/react-direction';
import i18n from 'i18next';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { useLocale } from '@/hooks/use-locale';

interface I18nProviderProps {
  children: ReactNode;
}

// Initialize i18n synchronously so server and client render identical text,
// preventing hydration mismatches. Language detection runs client-side after mount.
// Lazy loaders for non-routable languages (ar/es/ch)
const lazyResources: Record<
  string,
  () => Promise<{ default: Record<string, unknown> }>
> = {
  ar: () => import('@/i18n/messages/ar.json'),
  es: () => import('@/i18n/messages/es.json'),
  ch: () => import('@/i18n/messages/ch.json'),
};

async function ensureLanguageLoaded(code: string) {
  if (i18n.hasResourceBundle(code, 'translation')) return;
  const loader = lazyResources[code];
  if (loader) {
    const mod = await loader();
    i18n.addResourceBundle(code, 'translation', mod.default);
  }
}

if (!i18n.isInitialized) {
  const resources = {
    en: { translation: enTranslations },
    de: { translation: deTranslations },
  };

  i18n.use(initReactI18next).init({
    resources,
    lng: 'de',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

function I18nProvider({ children }: I18nProviderProps) {
  const [, forceUpdate] = useState(0);
  const { locale } = useLocale();
  // Each server request needs its own language so concurrent locales cannot race;
  // cloning during render also gives the client matching text for hydration.
  const [instance] = useState(() => i18n.cloneInstance({ lng: locale }));

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const language = I18N_LANGUAGES.find((lang) => lang.code === lng);
      if (language?.direction) {
        document.documentElement.setAttribute('dir', language.direction);
      }
      // Also save to localStorage as fallback
      localStorage.setItem('language', lng);
      if (i18n.language !== lng) i18n.changeLanguage(lng);
      forceUpdate((n) => n + 1);
    };

    instance.on('languageChanged', handleLanguageChange);
    if (i18n.language !== instance.language) i18n.changeLanguage(instance.language);
    return () => {
      instance.off('languageChanged', handleLanguageChange);
    };
  }, [instance]);

  useEffect(() => {
    if (locale !== instance.language) {
      ensureLanguageLoaded(locale).then(() => instance.changeLanguage(locale));
    }
  }, [locale, instance]);

  const currentLanguage =
    I18N_LANGUAGES.find((lang) => lang.code === (instance.language || 'de')) ||
    I18N_LANGUAGES[3]; // German

  return (
    <I18nextProvider i18n={instance}>
      <RadixDirectionProvider dir={currentLanguage.direction}>
        {children}
      </RadixDirectionProvider>
    </I18nextProvider>
  );
}

const useLanguage = () => {
  const router = useRouter();
  const { internalPathname } = useLocale();
  const { i18n: instance } = useTranslation();
  const [, startTransition] = useTransition();
  const [languageCode, setLanguageCode] = useState(instance.language || 'de');
  const [currentLanguage, setCurrentLanguage] = useState(
    () =>
      I18N_LANGUAGES.find((lang) => lang.code === (instance.language || 'de')) ||
      I18N_LANGUAGES[3],
  );

  useEffect(() => {
    const handleChange = (lng: string) => {
      setLanguageCode(lng);
      setCurrentLanguage(
        I18N_LANGUAGES.find((lang) => lang.code === lng) || I18N_LANGUAGES[3],
      );
    };

    // sync initial
    handleChange(instance.language || 'de');

    instance.on('languageChanged', handleChange);
    return () => {
      instance.off('languageChanged', handleChange);
    };
  }, [instance]);

  const changeLanguage = useCallback(
    async (code: string) => {
      // Ensure language bundle is loaded before switching
      await ensureLanguageLoaded(code);
      // Change i18next language first so all components re-render with new translations
      await instance.changeLanguage(code);
      // Then navigate to the localized URL path client-side
      const newPath = localizePathname(internalPathname, code as Locale);
      startTransition(() => {
        router.replace(newPath);
        // Server components (e.g. HomeHero) read x-locale via headers() and
        // bake translations into the RSC payload. router.replace alone may
        // serve the cached tree — refresh invalidates the Router Cache and
        // forces a fresh server render under the new locale.
        router.refresh();
      });
    },
    [instance, internalPathname, router, startTransition],
  );

  return {
    languageCode,
    language: currentLanguage,
    changeLanguage,
  };
};

export { I18nProvider, useLanguage };
